<script setup lang="ts">
import showdown from "showdown";
import { cn } from "~/lib/utils";
import InlineFileDownload from "./InlineFileDownload.vue";

const props = withDefaults(
  defineProps<{
    text?: string | null;
  }>(),
  {
    text: "",
  }
);

const parsedContent = computed(() => {
  if (!props.text) return "";

  let withPlaceholders = props.text;

  const domain = useRequestURL().host.replace("www.", "");
  const regexFilePattern = `<a\\b([^>]*?)href="((?:https?:\\/\\/(?:www\\.)?${domain})?\\/file\\/(\\d+))"(\\s[^>]*)?>(.*?)<\\/a>`;

  withPlaceholders = withPlaceholders?.replace(new RegExp(regexFilePattern, "gi"), '<a$1href="$2" data-file-id="$3"$4>$5</a>');

  const regexVideoPattern = /\[video\]\((https?:\/\/[^\s]+)\)/gi;
  withPlaceholders = withPlaceholders.replace(regexVideoPattern, '<a href="$1" data-video-src="$1">$1</a>');

  const converter = new showdown.Converter();
  return converter.makeHtml(withPlaceholders);
});

const container = ref<HTMLDivElement>();

type ProcessedBaseLink = {
  id: number;
  element: HTMLAnchorElement;
  type: "download" | "video" | "meta";
};

type ProcessedDownloadLink = ProcessedBaseLink & {
  type: "download";
  fileId: number;
  text: string;
};

type ProcessedVideoLink = ProcessedBaseLink & {
  type: "video";
  videoSrc: string;
};

type Meta = {
  [x: string]: any;
};

type ProcessedMetaLink = ProcessedBaseLink & {
  type: "meta";
  href: string;
  text: string;
  meta?: Meta;
  favicon?: string;
};

type ProcessedLink = ProcessedDownloadLink | ProcessedVideoLink | ProcessedMetaLink;

const processedLinks = ref<ProcessedLink[]>([]);

onMounted(() => {
  // Cerca il segnaposto solo all'interno del componente tramite ref
  if (container.value) {
    const links = container.value.getElementsByTagName("a");

    Array.from(links).forEach((link, index) => {
      if (link.hasAttribute("data-file-id")) {
        replaceDownloadLink(link, index);
      } else if (link.hasAttribute("data-video-src")) {
        replaceVideoLink(link, index);
      } else {
        replaceMetaLink(link, index);
      }
    });
  }
});

async function replaceMetaLink(link: HTMLAnchorElement, index: number) {
  const processedLink: ProcessedMetaLink = {
    id: index,
    href: link.href,
    text: link.innerHTML,
    element: link,
    type: "meta",
  };

  try {
    const response = await fetch(`/api/get-meta?url=${encodeURIComponent(link.href)}`);
    const meta = await response.json();

    processedLink.meta = meta || null;

    if (meta) {
      const favicon = meta.favicons?.find((f: object) => "rel" in f && (f.rel === "icon" || f.rel === "shortcut icon"));
      processedLink.favicon = favicon ? new URL(favicon.href, meta.url).href : null;
    }
  } catch (error) {}

  processedLinks.value.push(processedLink);

  const componentContainer = document.createElement("div");
  componentContainer.setAttribute("data-replace-id", index.toString());
  componentContainer.classList.add("inline-block");

  // Sostituisci il segnaposto con il componente montato
  link.replaceWith(componentContainer);
}

function replaceDownloadLink(link: HTMLAnchorElement, index: number) {
  const fileId = link.getAttribute("data-file-id");
  const text = link.innerHTML;

  // Crea il componente dinamicamente e montalo nel segnaposto
  if (!fileId) return;

  processedLinks.value.push({
    id: index,
    fileId: parseInt(fileId),
    text,
    element: link,
    type: "download",
  });

  const componentContainer = document.createElement("div");
  componentContainer.setAttribute("data-replace-id", index.toString());
  componentContainer.classList.add("inline-block");

  // Sostituisci il segnaposto con il componente montato
  link.replaceWith(componentContainer);
}

function replaceVideoLink(link: HTMLAnchorElement, index: number) {
  const videoSrc = link.getAttribute("data-video-src");

  processedLinks.value.push({
    id: index,
    videoSrc: videoSrc,
    element: link,
    type: "video",
  });

  const componentContainer = document.createElement("div");
  componentContainer.setAttribute("data-replace-id", index.toString());

  // Sostituisci il segnaposto con il componente montato
  link.replaceWith(componentContainer);
}
</script>

<template>
  <div
    :class="
      cn(
        'mb-8 print:mb-2 prose max-w-full break-words print:prose-p:break-inside-avoid print:prose-headings:break-after-avoid prose-a:text-primary prose-td:border prose-th:border prose-td:px-5 prose-th:px-5 prose-img:inline',
        $attrs.class
      )
    "
    ref="container"
  >
    <div v-html="parsedContent"></div>

    <template v-for="link in processedLinks" :key="link.id">
      <Teleport :to="`[data-replace-id='${link.id}']`">
        <InlineFileDownload v-if="link.type === 'download'" :fileId="link.fileId" :text="link.text" />
        <ClientOnly v-else-if="link.type === 'video'">
          <VideoPlayer
            class="border-2 border-neutral-300"
            :options="{
              controls: true,
              sources: [{ src: link.videoSrc, type: '' }],
              aspectRatio: '16:9',
            }"
          ></VideoPlayer>
        </ClientOnly>
        <template v-else-if="link.type === 'meta'">
          <NuxtLink v-if="!link.meta" :href="link.href" target="_blank" rel="noopener noreferrer" v-html="link.text"></NuxtLink>
          <HoverCard v-else>
            <HoverCardTrigger>
              <NuxtLink
                :class="
                  link.favicon &&
                  'inline-flex items-center bg-muted\\80 rounded-lg mx-1 ps-1 pe-2 no-underline font-semibold border border-primary-800\\5 print:border-none print:mx-0 print:p-0 print:font-normal'
                "
                :href="link.href"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img v-if="link.favicon" class="object-cover size-4 me-1.5 my-0 print:hidden" :src="link.favicon" :alt="link.meta?.title" />
                <span v-html="link.text === link.href && link.meta?.title ? link.meta['og:title'] || link.meta?.title : link.text"></span>
              </NuxtLink>
            </HoverCardTrigger>
            <HoverCardContent v-if="link.meta" class="w-96 p-0 print:hidden">
              <img v-if="link.meta['og:image']" class="w-full" :src="link.meta['og:image']" :alt="link.meta['og:title']" />
              <div class="flex justify-start space-x-4 p-5">
                <img v-if="link.favicon" class="object-cover size-5" :src="link.favicon" :alt="link.meta?.title" />
                <div class="space-y-2">
                  <h4 class="text-base font-semibold !-mt-1">
                    <NuxtLink class="link" :href="link.href" target="_blank" rel="noopener noreferrer">
                      {{ link.meta["og:title"] || link.meta?.title }}
                    </NuxtLink>
                  </h4>
                  <p class="text-sm text-muted-foreground">{{ link.meta?.description }}</p>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
        </template>
      </Teleport>
    </template>
  </div>
</template>
